'use client'

import { useState, useEffect, useMemo, useRef } from 'react'
import { useParams, useSearchParams } from 'next/navigation'
import { ChevronDown, Check, ChevronLeft, ChevronRight } from 'lucide-react'
import { createClient } from '@/lib/supabase/client'
import { createBooking, generateTimeSlots, dayHoursForDate, type DayHours } from '@/lib/db/bookings'
import { DEFAULT_BOOKING_SETTINGS, durationForPartySize, type BookingSettings } from '@/lib/db/settings'
import { detectWidgetLocale, getWidgetStrings, LOCALE_LABELS, WIDGET_LOCALES, type WidgetLocale } from '@/lib/widget-i18n'
import { buildBookingIcs, downloadIcs } from '@/lib/ical-client'

function slugToName(slug: string) {
  return slug.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')
}

function formatDate(d: Date, locale: WidgetLocale = 'de') {
  const tag = locale === 'tr' ? 'tr-TR' : locale === 'en' ? 'en-GB' : 'de-DE'
  return d.toLocaleDateString(tag, { weekday: 'long', day: 'numeric', month: 'long' })
}

function addDays(d: Date, n: number) {
  const r = new Date(d)
  r.setDate(r.getDate() + n)
  return r
}

function startOfDay(d: Date) {
  const r = new Date(d)
  r.setHours(0, 0, 0, 0)
  return r
}

// YYYY-MM-DD in the user's local zone. Plain toISOString() converts to UTC,
// which silently bumps the date backwards for any guest in CET after ~22:00
// local time — they pick "today" and the booking lands on yesterday.
function toLocalISODate(d: Date) {
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}

export default function WidgetPage() {
  const params = useParams()
  const slug = (params?.restaurant as string) ?? 'demo-restaurant'
  const [restaurantName, setRestaurantName] = useState(slugToName(slug))
  const [restaurantId, setRestaurantId] = useState<string | null>(null)
  // Locale: detected on mount from ?lang= or navigator.language, then
  // owner-pickable via a small DE/EN/TR switcher in the widget header.
  const [locale, setLocale] = useState<WidgetLocale>('de')
  useEffect(() => { setLocale(detectWidgetLocale()) }, [])
  const t = getWidgetStrings(locale)
  const [openingHours, setOpeningHours] = useState<Record<string, DayHours> | null>(null)
  const [bookingSettings, setBookingSettings] = useState<BookingSettings>(DEFAULT_BOOKING_SETTINGS)
  const [restaurantLoaded, setRestaurantLoaded] = useState(false)
  const [reservationsPaused, setReservationsPaused] = useState(false)

  const today = startOfDay(new Date())
  const [date, setDate] = useState(today)
  const [guests, setGuests] = useState(2)
  const [showGuestPicker, setShowGuestPicker] = useState(false)
  const [selectedTime, setSelectedTime] = useState<string | null>(null)
  // Day 22 — per-slot availability fetched from /api/availability.
  // Stored as a Map<time → free_seats> so the time grid can render
  // 'X frei' / 'Voll' hints without a per-slot fetch.
  const [availabilityMap, setAvailabilityMap] = useState<Map<string, number>>(new Map())
  const [availabilityLoading, setAvailabilityLoading] = useState(false)
  // Whole-day closure (Schliesstage) for the selected date — when set the
  // widget shows "Geschlossen" instead of time slots and blocks proceeding.
  const [closedInfo, setClosedInfo] = useState<{ reason: string | null } | null>(null)
  const [totalCapacity, setTotalCapacity] = useState<number | null>(null)
  // Day 51 — historically popular slots, badged for social proof.
  const [popularSlots, setPopularSlots] = useState<Set<string>>(new Set())
  const [step, setStep] = useState<'pick' | 'details' | 'confirm'>('pick')
  const [form, setForm] = useState({ firstName: '', lastName: '', phone: '', email: '', notes: '' })
  // Honeypot — a hidden field real guests never see or fill. Bots that
  // auto-fill every input give themselves away; the server drops those.
  const [hp, setHp] = useState('')
  // Day 28 — QR-Code from a printed table card sets ?tisch=<name>; we
  // prefill the booking note so the kitchen sees which table the guest
  // scanned from and the operator can prioritise that seat assignment.
  const searchParams = useSearchParams()
  useEffect(() => {
    const tisch = searchParams?.get('tisch')
    if (tisch && !form.notes.includes(tisch)) {
      setForm(prev => ({ ...prev, notes: prev.notes ? `${prev.notes} · Tisch ${tisch}` : `Tisch ${tisch}` }))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams])
  const [marketingConsent, setMarketingConsent] = useState(false)
  const [privacyConsent, setPrivacyConsent] = useState(false)

  // Day 32 — voucher cross-link when the restaurant enabled online
  // vouchers. MUST stay above the early returns below (the confirm-screen
  // `return` after submit) — declaring it after a conditional return broke
  // the Rules of Hooks (React #300) and crashed the widget on confirm.
  const [voucherEnabled, setVoucherEnabled] = useState(false)
  useEffect(() => {
    if (!slug) return
    let stop = false
    ;(async () => {
      try {
        const sb = createClient()
        const { data } = await sb
          .from('restaurants')
          .select('gift_voucher_settings')
          .eq('slug', slug)
          .maybeSingle()
        if (!stop) {
          const gv = (data as { gift_voucher_settings?: { enabled?: boolean } } | null)?.gift_voucher_settings
          setVoucherEnabled(Boolean(gv?.enabled))
        }
      } catch { /* ignore */ }
    })()
    return () => { stop = true }
  }, [slug])
  const [submitted, setSubmitted] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)
  // Day 46 — returning-guest recognition. When the guest finishes
  // typing their phone/email we ask the (privacy-safe) recognize
  // endpoint and, if they've booked here before, show a warm
  // "Willkommen zurück" banner. No PII is returned.
  const [returningGuest, setReturningGuest] = useState<{ visits: number } | null>(null)
  async function checkReturning(phone: string, email: string) {
    const p = phone.trim(), e = email.trim()
    if (p.replace(/[^0-9]/g, '').length < 6 && !e.includes('@')) { setReturningGuest(null); return }
    try {
      const qs = new URLSearchParams({ slug })
      if (p) qs.set('phone', p)
      if (e) qs.set('email', e)
      const r = await fetch(`/api/widget/recognize?${qs.toString()}`)
      const d = await r.json() as { returning?: boolean; visits?: number }
      setReturningGuest(d.returning ? { visits: d.visits ?? 1 } : null)
    } catch { setReturningGuest(null) }
  }

  // Day 22 — refetch availability whenever the picked date changes.
  useEffect(() => {
    if (!slug) return
    let cancelled = false
    const dateStr = (() => {
      const y = date.getFullYear(), m = String(date.getMonth() + 1).padStart(2, '0'), d = String(date.getDate()).padStart(2, '0')
      return `${y}-${m}-${d}`
    })()
    setAvailabilityLoading(true)
    ;(async () => {
      try {
        const r = await fetch(`/api/availability?slug=${encodeURIComponent(slug)}&date=${dateStr}`)
        if (!r.ok) { if (!cancelled) setAvailabilityMap(new Map()); return }
        const j = await r.json() as {
          total_capacity?: number
          closed?: boolean
          reason?: string | null
          by_slot?: Array<{ time: string; free_seats: number; popular?: boolean }>
        }
        if (!cancelled) {
          setClosedInfo(j.closed ? { reason: j.reason ?? null } : null)
          const map = new Map<string, number>()
          const pop = new Set<string>()
          for (const s of (j.by_slot ?? [])) { map.set(s.time, s.free_seats); if (s.popular) pop.add(s.time) }
          setAvailabilityMap(map)
          setPopularSlots(pop)
          setTotalCapacity(j.total_capacity ?? null)
        }
      } catch { if (!cancelled) { setAvailabilityMap(new Map()); setPopularSlots(new Set()) } }
      finally { if (!cancelled) setAvailabilityLoading(false) }
    })()
    return () => { cancelled = true }
  }, [slug, date])

  // Load restaurant info from DB
  useEffect(() => {
    let cancelled = false
    ;(async () => {
      try {
        const supabase = createClient()
        // Load known-stable columns first. Optional newer columns (e.g.
        // reservations_paused) are fetched separately so a missing column
        // doesn't blow up the whole query and leave the widget without data.
        const { data } = await supabase
          .from('restaurants')
          .select('id, name, opening_hours, booking_settings')
          .eq('slug', slug)
          .maybeSingle()
        if (!cancelled && data) {
          setRestaurantId(data.id)
          if (data.name) setRestaurantName(data.name)
          if (data.opening_hours) setOpeningHours(data.opening_hours as Record<string, DayHours>)
          if (data.booking_settings && typeof data.booking_settings === 'object') {
            setBookingSettings({ ...DEFAULT_BOOKING_SETTINGS, ...(data.booking_settings as object) })
          }
          // Optional pause flag — survives if column doesn't exist yet
          const { data: pauseRow, error: pauseErr } = await supabase
            .from('restaurants')
            .select('reservations_paused')
            .eq('id', data.id)
            .maybeSingle()
          if (!cancelled && !pauseErr && pauseRow) {
            if ((pauseRow as { reservations_paused?: boolean }).reservations_paused === true) {
              setReservationsPaused(true)
            }
          }
        }
      } catch { /* ignore */ }
      finally { if (!cancelled) setRestaurantLoaded(true) }
    })()
    return () => { cancelled = true }
  }, [slug])

  // Initialize guests within configured range
  useEffect(() => {
    setGuests(Math.max(bookingSettings.min_group, Math.min(bookingSettings.max_group, 2)))
  }, [bookingSettings.min_group, bookingSettings.max_group])

  // Compute time slots for selected date (use settings interval).
  // No fallback — show real DB state. If opening_hours not set, show clear message.
  const intervalMin = parseInt(bookingSettings.interval) || 30
  const dh = dayHoursForDate(openingHours, date)
  const TIMES = dh ? generateTimeSlots(dh.open, dh.close, intervalMin) : []
  const hasAnyDayConfigured = openingHours !== null && Object.keys(openingHours).some((k) => openingHours[k]?.active)
  const noHoursConfigured = restaurantLoaded && !hasAnyDayConfigured
  const isClosed = hasAnyDayConfigured && dh === null

  const guestOptions = useMemo(() => {
    const out: number[] = []
    for (let i = bookingSettings.min_group; i <= bookingSettings.max_group; i++) out.push(i)
    return out
  }, [bookingSettings.min_group, bookingSettings.max_group])

  const maxDate = useMemo(() => {
    const days = Number(bookingSettings.max_advance) || 365
    return addDays(today, days)
  }, [bookingSettings.max_advance, today])

  // Minimum allowed date as a primitive (epoch ms). When the owner has
  // disabled same-day reservations, this is tomorrow's midnight; else
  // today's midnight. Using a NUMBER (not a Date object) keeps useMemo
  // and downstream comparisons reference-stable so we don't trigger any
  // re-render churn — only changes when allow_same_day flips.
  const minDateMs = useMemo(() => {
    const t = startOfDay(new Date()).valueOf()
    return bookingSettings.allow_same_day === false ? t + 24 * 60 * 60 * 1000 : t
  }, [bookingSettings.allow_same_day])

  // One-time forward-bump when settings load with allow_same_day=false.
  // Guarded by a ref so this useEffect physically cannot run a second
  // time, even if React's strict mode double-invokes it or any dep
  // identity changes. Dep is the primitive boolean only — same value
  // means no re-run, so no infinite-loop trap.
  const bumpAppliedRef = useRef(false)
  useEffect(() => {
    if (bumpAppliedRef.current) return
    if (bookingSettings.allow_same_day !== false) return
    const todayMs = startOfDay(new Date()).valueOf()
    setDate(prev => {
      if (prev.valueOf() > todayMs) return prev  // already past today
      const tomorrow = startOfDay(new Date())
      tomorrow.setDate(tomorrow.getDate() + 1)
      return tomorrow
    })
    bumpAppliedRef.current = true
  }, [bookingSettings.allow_same_day])

  // Reset selected time when date changes if it's no longer in valid slots
  useEffect(() => {
    if (selectedTime && TIMES.length > 0 && !TIMES.includes(selectedTime)) setSelectedTime(null)
  }, [date, openingHours]) // eslint-disable-line react-hooks/exhaustive-deps

  const name = restaurantName

  function handleBook() {
    if (!selectedTime) return
    setStep('details')
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setSubmitError(null)

    if (reservationsPaused) {
      setSubmitError('Aktuell nehmen wir keine Reservierungen entgegen. Bitte versuchen Sie es später noch einmal.')
      return
    }

    // No selectedTime means user is opting into the waitlist instead of
    // a regular booking — the day is closed or fully booked.
    const isWaitlistFlow = !selectedTime

    // Validate required fields per booking settings
    const required = bookingSettings.required_fields
    if (!form.firstName.trim()) { setSubmitError('Vorname ist erforderlich.'); return }
    if (isWaitlistFlow) {
      if (!form.phone.trim() && !form.email.trim()) {
        setSubmitError('Bitte Telefon oder E-Mail angeben, damit wir Sie benachrichtigen können.')
        return
      }
    } else {
      if (required.phone && !form.phone.trim()) { setSubmitError('Telefon ist erforderlich.'); return }
      if (required.email && !form.email.trim()) { setSubmitError('E-Mail ist erforderlich.'); return }
    }

    // Format checks (German messages, even when fields are optional but filled)
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    if (form.email.trim() && !emailRegex.test(form.email.trim())) {
      setSubmitError('Bitte eine gültige E-Mail-Adresse eingeben (z. B. name@beispiel.de).')
      return
    }
    if (form.phone.trim()) {
      const digits = form.phone.replace(/\D/g, '')
      if (digits.length < 6) {
        setSubmitError('Bitte eine gültige Telefonnummer eingeben (mindestens 6 Ziffern).')
        return
      }
    }
    // DSGVO: explicit consent to data processing is required before booking.
    if (!privacyConsent) { setSubmitError(t.privacyRequired); return }

    setSubmitting(true)
    try {
      if (isWaitlistFlow) {
        const wr = await fetch('/api/waitlist', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            restaurant_slug: slug,
            date: toLocalISODate(date),
            preferred_time: null,
            guests,
            guest_first_name: form.firstName.trim(),
            guest_last_name: form.lastName.trim() || undefined,
            guest_email: form.email.trim() || undefined,
            guest_phone: form.phone.trim() || undefined,
            notes: form.notes.trim() || undefined,
          }),
        })
        const wd = await wr.json() as { ok?: boolean; position?: number; error?: string }
        if (!wr.ok || !wd.ok) throw new Error(wd.error || `HTTP ${wr.status}`)
        setSubmitted(true)
        setStep('confirm')
        return
      }

      // Confirm mode → status
      let status: 'Ausstehend' | 'Bestätigt' = 'Ausstehend'
      if (bookingSettings.confirm_mode === 'auto') status = 'Bestätigt'
      else if (bookingSettings.confirm_mode === 'upto' && guests <= bookingSettings.upto_count) status = 'Bestätigt'

      // Server-side endpoint inserts + sends confirmation email in one shot
      const r = await fetch('/api/bookings/public-create', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          restaurant_slug: slug,
          guest_first_name: form.firstName.trim(),
          guest_last_name: form.lastName.trim() || undefined,
          guest_email: form.email.trim() || undefined,
          guest_phone: form.phone.trim() || undefined,
          date: toLocalISODate(date),
          time: selectedTime,
          guests,
          notes: form.notes.trim() || undefined,
          status,
          marketing_consent: marketingConsent,
          hp,
        }),
      })
      const d = await r.json() as { ok?: boolean; error?: string }
      if (!r.ok || !d.ok) throw new Error(d.error || `HTTP ${r.status}`)
      setSubmitted(true)
      setStep('confirm')
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : 'Reservierung fehlgeschlagen')
    } finally {
      setSubmitting(false)
    }
  }

  if (submitted && step === 'confirm') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-neutral-50 px-4" style={{ colorScheme: 'light' }}>
        <div className="w-full max-w-sm text-center space-y-4">
          <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-green-100">
            <Check className="h-7 w-7 text-green-600" strokeWidth={2.5} />
          </div>
          <h2 className="text-xl font-bold text-neutral-900">
            {bookingSettings.confirm_mode === 'manual' ? t.pendingTitle : t.confirmedTitle}
          </h2>
          <p className="text-sm text-neutral-700 font-medium">
            {guests} {guests === 1 ? t.guestsSingular : t.guests} · {selectedTime} · {formatDate(date, locale)}
          </p>
          <div className="mx-auto inline-flex items-center gap-1.5 rounded-full bg-brand-50 border border-brand-100 px-3 py-1 text-xs text-brand-900">
            <svg className="h-3.5 w-3.5 flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
              <circle cx="12" cy="12" r="10" />
              <path d="M12 6v6l4 2" />
            </svg>
            {t.reservedFor} <strong>{durationForPartySize(bookingSettings, guests)} {t.minutes}</strong>
            {bookingSettings.show_end_time && selectedTime ? (
              <>
                <span className="text-brand-400">·</span>
                <span>{t.until} {(() => {
                  const [h, m] = selectedTime.split(':').map(Number)
                  const end = new Date(0, 0, 0, h, m + durationForPartySize(bookingSettings, guests))
                  return `${String(end.getHours()).padStart(2, '0')}:${String(end.getMinutes()).padStart(2, '0')}`
                })()}</span>
              </>
            ) : null}
          </div>
          {bookingSettings.confirm_mode === 'manual' ? (
            <p className="text-xs text-neutral-400 mt-2">
              {t.pendingReviewHint}
            </p>
          ) : form.email && (
            <p className="text-xs text-neutral-400 mt-2">
              {t.confirmSentTo.split('{email}')[0]}<strong>{form.email}</strong>{t.confirmSentTo.split('{email}')[1]}
            </p>
          )}

          {/* Day 10: Add to calendar — generates an .ics on the fly and
              hands it off to the OS. iOS opens Calendar.app directly,
              Android prompts Google Calendar, desktop downloads file. */}
          {selectedTime && (
            <button
              type="button"
              onClick={() => {
                const ics = buildBookingIcs({
                  restaurantName: name,
                  date: toLocalISODate(date),
                  time: selectedTime,
                  durationMinutes: durationForPartySize(bookingSettings, guests),
                  guests,
                  guestName: [form.firstName, form.lastName].filter(Boolean).join(' ') || null,
                  notes: form.notes || null,
                })
                downloadIcs(`reservierung-${name.replace(/\s+/g, '-').toLowerCase()}-${toLocalISODate(date)}`, ics)
              }}
              className="w-full inline-flex items-center justify-center gap-2 rounded-xl border border-brand-200 bg-white px-4 py-2.5 text-sm font-semibold text-brand-700 hover:bg-brand-50 transition-colors">
              📅 {t.addToCalendar}
            </button>
          )}

          <div className="pt-4 space-y-3">
            <p className="text-[11px] text-neutral-500">
              {t.afterVisit}{' '}
              <a href={`/${slug}/bewerten`} className="font-medium text-brand-600 hover:underline">
                {t.leaveReview}
              </a>
            </p>
            <p className="text-xs text-neutral-400">
              {t.poweredBy}{' '}
              <a href="https://rezaro.de" target="_blank" rel="noopener noreferrer"
                className="font-semibold text-brand-600 hover:underline">
                Rezaro
              </a>
            </p>
          </div>
        </div>
      </div>
    )
  }

  const stepLabel = { pick: t.stepDateTime, details: t.stepDetails, confirm: t.stepConfirm }
  const stepIndex = step === 'pick' ? 1 : step === 'details' ? 2 : 3
  const stepWord = t.step
  return (
    <div className="min-h-screen bg-neutral-50 flex flex-col items-center justify-start sm:justify-center px-4 py-4 sm:py-8" style={{ colorScheme: 'light' }}>
      <div className="w-full max-w-sm bg-white rounded-2xl shadow-lg overflow-hidden border border-neutral-100">
        {/* Header */}
        <div className="relative bg-white border-b border-neutral-100 px-5 py-4 text-center">
          <p className="text-base font-bold text-neutral-900">{name}</p>
          <p className="text-[11px] uppercase tracking-widest text-neutral-400 mt-0.5">
            {t.onlineReservation}
          </p>
          {voucherEnabled && (
            <a href={`/${slug}/gutschein`}
              className="absolute left-3 top-3 inline-flex items-center gap-1 rounded-full bg-brand-50 border border-brand-200 px-2 py-1 text-[10px] font-semibold text-brand-700 hover:bg-brand-100">
              🎁 {locale === 'de' ? 'Gutschein' : locale === 'tr' ? 'Hediye' : 'Gift'}
            </a>
          )}
          {/* Locale switcher — compact dropdown so it stays small no matter
              how many languages exist (5: DE/EN/FR/IT/TR) and never overlaps
              the centered title on the narrow card. */}
          <select
            value={locale}
            onChange={(e) => setLocale(e.target.value as WidgetLocale)}
            aria-label="Sprache / Language"
            className="absolute right-3 top-3 rounded-full border border-neutral-200 bg-white py-1 pl-2.5 pr-1.5 text-[10px] font-semibold text-neutral-700 cursor-pointer focus:outline-none focus:ring-2 focus:ring-brand-500">
            {WIDGET_LOCALES.map((l) => (
              <option key={l} value={l}>{LOCALE_LABELS[l]}</option>
            ))}
          </select>
        </div>

        {/* Progress indicator */}
        <div className="bg-white px-5 pt-3 pb-2">
          <div className="flex items-center justify-between text-[11px]">
            <span className="font-semibold text-brand-700">{stepWord} {stepIndex} / 3</span>
            <span className="text-neutral-500">{stepLabel[step]}</span>
          </div>
          <div className="mt-1.5 flex gap-1">
            {[1, 2, 3].map((s) => (
              <div key={s}
                className={`h-1 flex-1 rounded-full transition-colors ${s <= stepIndex ? 'bg-brand-600' : 'bg-neutral-200'}`}
              />
            ))}
          </div>
        </div>

        {step === 'pick' && (
          <>
            {/* Guest + Date row */}
            <div className="flex border-b border-neutral-100">
              {/* Guests */}
              <div className="relative flex-1 border-r border-neutral-100">
                <button
                  type="button"
                  onClick={() => setShowGuestPicker(v => !v)}
                  className="w-full flex items-center justify-center gap-1.5 px-4 py-3 text-sm font-medium text-neutral-700 hover:bg-neutral-50 transition-colors"
                >
                  {guests} {guests === 1 ? t.guestsSingular : t.guests}
                  <ChevronDown className="h-3.5 w-3.5 text-neutral-400" />
                </button>
                {showGuestPicker && (
                  <div className="absolute left-0 top-full z-10 w-40 rounded-xl border border-neutral-200 bg-white shadow-lg py-1 max-h-64 overflow-y-auto">
                    {guestOptions.map(n => (
                      <button key={n} type="button"
                        onClick={() => { setGuests(n); setShowGuestPicker(false) }}
                        className={`w-full px-4 py-2 text-left text-sm hover:bg-neutral-50 transition-colors ${guests === n ? 'font-semibold text-brand-600' : 'text-neutral-700'}`}
                      >
                        {n} {n === 1 ? t.guestsSingular : t.guests}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Date — pfeile sind jetzt deutlich sichtbar (border + brand-Tönung statt nur Hover) */}
              <div className="flex flex-1 items-center justify-between px-2 py-2 gap-2">
                <button type="button" aria-label="Vorheriger Tag"
                  onClick={() => setDate(d => addDays(d, -1))}
                  disabled={date.valueOf() <= minDateMs}
                  className="h-9 w-9 flex flex-shrink-0 items-center justify-center rounded-full border border-neutral-300 bg-white text-brand-700 hover:bg-brand-50 hover:border-brand-600 disabled:opacity-30 disabled:hover:bg-white disabled:hover:border-neutral-300 active:scale-95 transition-all">
                  <ChevronLeft className="h-5 w-5" strokeWidth={2.2} />
                </button>
                <span className="text-sm font-semibold text-neutral-900 tabular-nums">
                  {(() => {
                    const dMs = date.valueOf()
                    const tMs = startOfDay(new Date()).valueOf()
                    if (dMs === tMs) return t.today
                    if (dMs === tMs + 24 * 60 * 60 * 1000) return t.tomorrow
                    const localeTag = locale === 'tr' ? 'tr-TR' : locale === 'en' ? 'en-GB' : 'de-DE'
                    return date.toLocaleDateString(localeTag, { day: 'numeric', month: 'short' })
                  })()}
                </span>
                <button type="button" aria-label="Nächster Tag"
                  onClick={() => setDate(d => addDays(d, 1))}
                  disabled={date >= maxDate}
                  className="h-9 w-9 flex flex-shrink-0 items-center justify-center rounded-full border border-neutral-300 bg-white text-brand-700 hover:bg-brand-50 hover:border-brand-600 disabled:opacity-30 disabled:hover:bg-white disabled:hover:border-neutral-300 active:scale-95 transition-all">
                  <ChevronRight className="h-5 w-5" strokeWidth={2.2} />
                </button>
              </div>
            </div>

            {/* Day 22 — live availability strip. Only shows when we
                actually know the total capacity (otherwise the number
                would be meaningless). */}
            {totalCapacity != null && totalCapacity > 0 && availabilityMap.size > 0 && (() => {
              const tightCount = Array.from(availabilityMap.values()).filter(f => f > 0 && f < guests + 6).length
              const fullCount = Array.from(availabilityMap.values()).filter(f => f === 0).length
              const totalSlots = availabilityMap.size
              const allFull = fullCount === totalSlots
              return (
                <div className={`mx-4 mt-1 mb-2 rounded-xl px-3 py-2 flex items-center justify-between gap-2 text-xs ${
                  allFull
                    ? 'bg-red-50 border border-red-200 text-red-900'
                    : tightCount > 0
                      ? 'bg-amber-50 border border-amber-200 text-amber-900'
                      : 'bg-emerald-50 border border-emerald-200 text-emerald-900'
                }`}>
                  <span className="font-semibold">
                    {allFull ? t.availFull
                      : tightCount > totalSlots / 2 ? t.availTight
                      : t.availFree}
                  </span>
                  <span className="opacity-80 tabular-nums">
                    {availabilityLoading ? '…' : t.capacityMeta.replace('{cap}', String(totalCapacity)).replace('{slots}', String(totalSlots))}
                  </span>
                </div>
              )
            })()}

            {/* Time slots */}
            <div className="px-4 py-4 max-h-64 overflow-y-auto">
              {!restaurantLoaded ? (
                <p className="text-xs text-neutral-400 text-center py-6">…</p>
              ) : closedInfo ? (
                <div className="text-center py-8 px-2">
                  <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-neutral-200">
                    <span className="text-2xl">🔒</span>
                  </div>
                  <p className="text-sm text-neutral-900 font-semibold">Geschlossen</p>
                  <p className="text-xs text-neutral-500 mt-1.5">
                    {closedInfo.reason || 'An diesem Tag nehmen wir keine Reservierungen entgegen. Bitte wählen Sie ein anderes Datum.'}
                  </p>
                </div>
              ) : reservationsPaused ? (
                <div className="text-center py-8 px-2">
                  <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-red-100">
                    <span className="text-2xl">🚫</span>
                  </div>
                  <p className="text-sm text-neutral-900 font-semibold">{t.pausedTitle}</p>
                  <p className="text-xs text-neutral-500 mt-1.5">{t.pausedBody}</p>
                </div>
              ) : noHoursConfigured ? (
                <div className="text-center py-6 px-2">
                  <p className="text-sm text-neutral-700 font-medium">{t.notConfigured}</p>
                  <p className="text-xs text-neutral-500 mt-1.5">{t.adminHint}</p>
                </div>
              ) : isClosed ? (
                <div className="text-center py-6 space-y-3">
                  <p className="text-sm text-neutral-700 font-medium">{t.closedToday}</p>
                  <button
                    type="button"
                    onClick={() => setStep('details')}
                    className="text-xs text-brand-600 hover:underline">
                    {t.waitlistSwitch}
                  </button>
                </div>
              ) : TIMES.length === 0 ? (
                <div className="text-center py-6 space-y-3">
                  <p className="text-sm text-neutral-500">{t.noSlots}</p>
                  <button
                    type="button"
                    onClick={() => setStep('details')}
                    className="text-xs text-brand-600 hover:underline">
                    {t.waitlistSwitch}
                  </button>
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-2">
                  {TIMES.map((slot) => {
                    const isToday = date.toDateString() === today.toDateString()
                    const now = new Date()
                    const nowMin = now.getHours() * 60 + now.getMinutes()
                    const [h, m] = slot.split(':').map(Number)
                    const slotMin = h * 60 + m
                    const minAdvance = parseInt(bookingSettings.min_advance) || 0
                    const tooLate = isToday && slotMin <= nowMin + minAdvance
                    const free = availabilityMap.get(slot)
                    const full = free !== undefined && free < guests
                    const tight = free !== undefined && free >= guests && free < guests + 6
                    const unavailable = tooLate || full
                    return (
                      <button
                        key={slot}
                        type="button"
                        disabled={unavailable}
                        onClick={() => setSelectedTime(slot)}
                        className={`rounded-xl border py-2 text-sm font-medium transition-colors flex flex-col items-center justify-center gap-0.5
                          ${unavailable ? 'border-transparent bg-neutral-50 text-neutral-300 cursor-not-allowed' :
                            selectedTime === slot
                              ? 'border-brand-600 bg-brand-600 text-white'
                              : tight
                                ? 'border-amber-300 bg-amber-50 text-amber-900 hover:border-amber-400'
                                : 'border-neutral-200 text-neutral-700 hover:border-brand-400 hover:text-brand-600'
                          }`}
                      >
                        <span className="flex items-center gap-1">
                          {tooLate ? <span className="text-[11px]">{t.notAvailable}</span> : slot}
                          {!tooLate && !full && popularSlots.has(slot) && selectedTime !== slot && (
                            <span title={t.popular} className="text-[9px]">⭐</span>
                          )}
                        </span>
                        {!tooLate && free !== undefined && (
                          <span className="text-[10px] opacity-80 leading-none">
                            {full ? t.seatsFull : tight ? t.seatsTight.replace('{n}', String(free)) : t.seatsFree.replace('{n}', String(free))}
                          </span>
                        )}
                      </button>
                    )
                  })}
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="px-4 pb-4">
              <button
                type="button"
                disabled={!selectedTime || !!closedInfo}
                onClick={handleBook}
                className="w-full rounded-xl bg-brand-600 py-3 text-sm font-semibold text-white hover:bg-brand-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                {t.continue}
              </button>
            </div>

            <div className="pb-3 text-center">
              <p className="text-[11px] text-neutral-400">
                Powered by{' '}
                <a href="https://rezaro.de" target="_blank" rel="noopener noreferrer"
                  className="font-semibold text-neutral-600 hover:text-brand-600 hover:underline">
                  Rezaro
                </a>
              </p>
            </div>
          </>
        )}

        {step === 'details' && (
          <form onSubmit={handleSubmit} className="px-5 py-4 space-y-3">
            <p className="text-xs text-neutral-500 pb-1">
              {(() => {
                const guestLabel = guests === 1 ? t.guestsSingular : t.guests
                if (!selectedTime) return `${guests} ${guestLabel} · ${formatDate(date, locale)}`
                const [h, m] = selectedTime.split(':').map(Number)
                const end = new Date(0, 0, 0, h, m + durationForPartySize(bookingSettings, guests))
                const endHH = String(end.getHours()).padStart(2, '0')
                const endMM = String(end.getMinutes()).padStart(2, '0')
                return `${guests} ${guestLabel} · ${selectedTime}–${endHH}:${endMM} · ${formatDate(date, locale)}`
              })()}
            </p>
            <div className="rounded-lg bg-brand-50 border border-brand-100 px-3 py-2 text-xs text-brand-900 flex items-center gap-2">
              <svg className="h-3.5 w-3.5 flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
                <circle cx="12" cy="12" r="10" />
                <path d="M12 6v6l4 2" />
              </svg>
              <span>
                Sie reservieren für <strong>{durationForPartySize(bookingSettings, guests)} Minuten</strong>.
                Bitte planen Sie diese Zeit ein.
              </span>
            </div>

            {returningGuest && (
              <div className="rounded-lg bg-emerald-50 border border-emerald-200 px-3 py-2 text-xs text-emerald-900">
                <span className="font-semibold">{t.welcomeBack}</span> {t.welcomeBackHint}
              </div>
            )}

            {/* Honeypot: off-screen, not announced, never tab-focusable.
                Real guests leave it empty; bots fill it and get dropped. */}
            <input
              type="text"
              name="company"
              tabIndex={-1}
              autoComplete="off"
              aria-hidden="true"
              value={hp}
              onChange={e => setHp(e.target.value)}
              style={{ position: 'absolute', left: '-9999px', width: 1, height: 1, opacity: 0 }}
            />

            <div className="grid grid-cols-2 gap-3">
              <div className="flex flex-col gap-1">
                <label className="text-xs font-medium text-neutral-600">{t.firstName} *</label>
                <input required value={form.firstName}
                  onChange={e => setForm(f => ({ ...f, firstName: e.target.value }))}
                  className="h-9 rounded-lg border border-neutral-200 bg-white px-3 text-sm text-neutral-900 placeholder:text-neutral-400 focus:outline-none focus:ring-2 focus:ring-brand-500" />
              </div>
              <div className="flex flex-col gap-1">
                <label className="text-xs font-medium text-neutral-600">{t.lastName}</label>
                <input value={form.lastName}
                  onChange={e => setForm(f => ({ ...f, lastName: e.target.value }))}
                  className="h-9 rounded-lg border border-neutral-200 bg-white px-3 text-sm text-neutral-900 placeholder:text-neutral-400 focus:outline-none focus:ring-2 focus:ring-brand-500" />
              </div>
            </div>

            <div className="flex flex-col gap-1">
              <label className="text-xs font-medium text-neutral-600">
                {t.email}{bookingSettings.required_fields.email ? ' *' : ''}
              </label>
              <input type="email" required={bookingSettings.required_fields.email} value={form.email}
                onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
                onBlur={e => checkReturning(form.phone, e.target.value)}
                className="h-9 rounded-lg border border-neutral-200 bg-white px-3 text-sm text-neutral-900 placeholder:text-neutral-400 focus:outline-none focus:ring-2 focus:ring-brand-500" />
            </div>

            <div className="flex flex-col gap-1">
              <label className="text-xs font-medium text-neutral-600">
                {t.phone}{bookingSettings.required_fields.phone ? ' *' : ''}
              </label>
              <input type="tel" required={bookingSettings.required_fields.phone} value={form.phone}
                onChange={e => setForm(f => ({ ...f, phone: e.target.value }))}
                onBlur={e => checkReturning(e.target.value, form.email)}
                placeholder={t.phonePlaceholder}
                className="h-9 rounded-lg border border-neutral-200 bg-white px-3 text-sm text-neutral-900 placeholder:text-neutral-400 focus:outline-none focus:ring-2 focus:ring-brand-500" />
            </div>

            <div className="flex flex-col gap-1">
              <label className="text-xs font-medium text-neutral-600">{t.notes}</label>
              <textarea rows={2} value={form.notes}
                onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
                placeholder={t.notesPlaceholder}
                className="rounded-lg border border-neutral-200 bg-white px-3 py-2 text-sm text-neutral-900 placeholder:text-neutral-400 resize-none focus:outline-none focus:ring-2 focus:ring-brand-500" />
            </div>

            <label className="flex items-start gap-2 cursor-pointer pt-0.5">
              <input
                type="checkbox"
                checked={privacyConsent}
                onChange={(e) => setPrivacyConsent(e.target.checked)}
                className="mt-0.5 h-3.5 w-3.5 accent-brand-600 flex-shrink-0"
                aria-required="true"
              />
              <span className="text-[11px] text-neutral-600 leading-snug">
                {t.privacyConsent.split('{link}')[0]}
                <a href={`/${slug}/datenschutz`} target="_blank" rel="noopener noreferrer"
                  onClick={(e) => e.stopPropagation()}
                  className="text-brand-700 underline underline-offset-2">
                  {t.privacyLink}
                </a>
                {t.privacyConsent.split('{link}')[1]}
              </span>
            </label>

            <label className="flex items-start gap-2 cursor-pointer pt-0.5">
              <input
                type="checkbox"
                checked={marketingConsent}
                onChange={(e) => setMarketingConsent(e.target.checked)}
                className="mt-0.5 h-3.5 w-3.5 accent-brand-600 flex-shrink-0"
              />
              <span className="text-[11px] text-neutral-500 leading-snug">
                {t.marketingConsent}
              </span>
            </label>

            {submitError && <p className="text-[12px] text-red-600">{submitError}</p>}

            <div className="flex gap-2 pt-1">
              <button type="button" onClick={() => setStep('pick')}
                className="flex-1 h-10 rounded-xl border border-neutral-200 text-sm text-neutral-600 hover:bg-neutral-50 transition-colors">
                {t.back}
              </button>
              <button type="submit" disabled={submitting}
                className="flex-1 h-10 rounded-xl bg-brand-600 text-sm font-semibold text-white hover:bg-brand-700 transition-colors disabled:opacity-60">
                {submitting ? t.submitting : t.submit}
              </button>
            </div>

            <p className="text-[11px] text-neutral-400 text-center pb-1">
              {t.poweredBy}{' '}
              <a href="https://rezaro.de" target="_blank" rel="noopener noreferrer"
                className="font-semibold text-neutral-600 hover:text-brand-600 hover:underline">
                Rezaro
              </a>
            </p>
          </form>
        )}
      </div>
    </div>
  )
}
